import { useCallback, useEffect, useState } from 'react';

export const DASHBOARD_SECTION_VISIBILITY_STORAGE_KEY = 'womanalytics.dashboard.sectionVisibility';

export const DASHBOARD_SECTION_IDS = [
  'statCards',
  'primaryCharts',
  'chatAdsCharts',
  'retentionTable',
  'appMetricsTable',
] as const;

export type DashboardSectionId = (typeof DASHBOARD_SECTION_IDS)[number];

export type DashboardSectionVisibility = Record<DashboardSectionId, boolean>;

export const DASHBOARD_SECTION_LABELS: Record<DashboardSectionId, string> = {
  statCards: 'Özet kartlar',
  primaryCharts: 'Ana grafikler ve tablolar',
  chatAdsCharts: 'Sohbet ve reklam grafikleri',
  retentionTable: 'Retention tablosu',
  appMetricsTable: 'Uygulama metrikleri panosu',
};

export const DEFAULT_DASHBOARD_SECTION_VISIBILITY: DashboardSectionVisibility = {
  statCards: true,
  primaryCharts: true,
  chatAdsCharts: true,
  retentionTable: true,
  appMetricsTable: true,
};

function normalizeVisibility(parsed: unknown): DashboardSectionVisibility | null {
  if (parsed === null || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;
  const next = { ...DEFAULT_DASHBOARD_SECTION_VISIBILITY };
  for (const id of DASHBOARD_SECTION_IDS) {
    const v = o[id];
    if (typeof v === 'boolean') {
      next[id] = v;
    }
  }
  return next;
}

function loadFromStorage(): DashboardSectionVisibility {
  try {
    const raw = localStorage.getItem(DASHBOARD_SECTION_VISIBILITY_STORAGE_KEY);
    if (raw) {
      const n = normalizeVisibility(JSON.parse(raw));
      if (n) return n;
    }
  } catch {
    /* ignore */
  }
  return { ...DEFAULT_DASHBOARD_SECTION_VISIBILITY };
}

function saveToStorage(v: DashboardSectionVisibility) {
  try {
    localStorage.setItem(DASHBOARD_SECTION_VISIBILITY_STORAGE_KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

export function useDashboardSectionVisibility() {
  const [visibility, setVisibilityState] = useState<DashboardSectionVisibility>(() =>
    loadFromStorage()
  );

  useEffect(() => {
    const sync = () => setVisibilityState(loadFromStorage());
    window.addEventListener('storage', sync);
    window.addEventListener('dashboard-section-visibility-reset', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('dashboard-section-visibility-reset', sync);
    };
  }, []);

  const setVisible = useCallback((id: DashboardSectionId, visible: boolean) => {
    setVisibilityState(prev => {
      const next = { ...prev, [id]: visible };
      saveToStorage(next);
      return next;
    });
  }, []);

  const toggle = useCallback((id: DashboardSectionId) => {
    setVisibilityState(prev => {
      const next = { ...prev, [id]: !prev[id] };
      saveToStorage(next);
      return next;
    });
  }, []);

  const showAll = useCallback(() => {
    setVisibilityState(() => {
      saveToStorage(DEFAULT_DASHBOARD_SECTION_VISIBILITY);
      return { ...DEFAULT_DASHBOARD_SECTION_VISIBILITY };
    });
  }, []);

  const hideAll = useCallback(() => {
    const allOff = DASHBOARD_SECTION_IDS.reduce(
      (acc, id) => {
        acc[id] = false;
        return acc;
      },
      {} as DashboardSectionVisibility
    );
    setVisibilityState(() => {
      saveToStorage(allOff);
      return allOff;
    });
  }, []);

  return { visibility, setVisible, toggle, showAll, hideAll };
}

export function resetDashboardSectionVisibilityStorage(): boolean {
  try {
    localStorage.removeItem(DASHBOARD_SECTION_VISIBILITY_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('dashboard-section-visibility-reset'));
    return true;
  } catch {
    return false;
  }
}
